import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Moon, Sun } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { applyResolvedTheme, applyThemePreference, readThemePreference } from '../../lib/theme';
import { PrivacyChoicesButton } from '../../components/consent/PrivacyChoicesButton';
import {
  parsePublicStatusTheme,
  publicStatusThemeStorageKey,
  resolvePublicStatusTheme,
  togglePublicStatusTheme,
  type ConfiguredPublicStatusTheme,
  type PublicStatusTheme,
} from './publicStatusTheme';

interface PublicStatusBranding {
  logo?: string;
  primaryColor: string;
  backgroundColor: string;
  textColor: string;
  theme: ConfiguredPublicStatusTheme;
}

interface PublicStatusSettings {
  showUptime: boolean;
  showResponseTime: boolean;
  uptimeDays: number;
  showHistoricalIncidents: boolean;
  historicalIncidentDays: number;
  showSubscription: boolean;
  allowEmailSubscription: boolean;
  allowRSSSubscription: boolean;
  showPoweredBy: boolean;
}

interface PublicStatusComponent {
  id: string;
  name: string;
  description?: string;
  status: string;
  showUptime: boolean;
  uptime?: {
    percentage: number;
    data: Array<{
      date: string;
      percentage: number;
      status: string;
    }>;
  };
}

interface PublicIncident {
  _id: string;
  title: string;
  status: string;
  impact: string;
  startedAt: string;
  resolvedAt?: string | null;
  updates: Array<{
    id: string;
    status: string;
    message: string;
    createdAt: string;
  }>;
}

interface PublicMaintenanceWindow {
  _id: string;
  title: string;
  description: string;
  status: string;
  scheduledStart: string;
  scheduledEnd: string;
}

interface PublicStatusPayload {
  page: {
    name: string;
    description: string;
    slug: string;
    branding: PublicStatusBranding;
    settings: PublicStatusSettings;
  };
  components: PublicStatusComponent[];
  incidents: {
    active: PublicIncident[];
    historical: PublicIncident[];
  };
  maintenance: {
    upcoming: PublicMaintenanceWindow[];
    inProgress: PublicMaintenanceWindow[];
  };
}

async function publicJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'omit',
    headers: {
      Accept: 'application/json',
      ...init?.headers,
    },
  });
  const payload = (await response.json().catch(() => null)) as
    | (T & { error?: { message?: string } })
    | null;

  if (!response.ok) {
    throw new Error(payload?.error?.message || 'The public status page is unavailable.');
  }

  return payload as T;
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function readVisitorTheme(slug: string): PublicStatusTheme | null {
  try {
    return parsePublicStatusTheme(window.localStorage.getItem(publicStatusThemeStorageKey(slug)));
  } catch {
    return null;
  }
}

function usePrefersDark(): boolean {
  const [prefersDark, setPrefersDark] = useState(
    () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mediaQuery) {
      return;
    }

    const handleChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersDark;
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

function statusTone(status: string, isDark: boolean): string {
  if (status === 'operational' || status === 'up' || status === 'resolved') {
    return isDark ? 'text-emerald-300' : 'text-emerald-700';
  }
  if (status === 'degraded' || status === 'partial_outage' || status === 'maintenance') {
    return isDark ? 'text-amber-300' : 'text-amber-700';
  }
  return isDark ? 'text-rose-300' : 'text-rose-700';
}

function latestIncidentUpdate(incident: PublicIncident) {
  return incident.updates[incident.updates.length - 1];
}

function LoadingState() {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-950 px-6 text-slate-100">
      <p role="status" className="text-sm text-slate-300">Loading public status…</p>
      <PrivacyChoicesButton variant="link" />
    </main>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-950 px-6 text-slate-100">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold text-white">Status page unavailable</h1>
        <p className="mt-3 text-sm text-slate-300">{message}</p>
        <div className="mt-6 flex justify-center">
          <PrivacyChoicesButton variant="link" />
        </div>
      </div>
    </main>
  );
}

export function PublicStatusPage() {
  const { slug = '' } = useParams();
  const apiPath = `/api/public/status/${encodeURIComponent(slug)}`;
  const prefersDark = usePrefersDark();
  const [visitorTheme, setVisitorTheme] = useState<PublicStatusTheme | null>(() =>
    readVisitorTheme(slug)
  );
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    setVisitorTheme(readVisitorTheme(slug));
  }, [slug]);

  const statusQuery = useQuery({
    queryKey: ['public-status', slug],
    queryFn: () => publicJson<PublicStatusPayload>(apiPath),
    enabled: Boolean(slug),
    refetchInterval: 60_000,
  });

  const configuredTheme = statusQuery.data?.page.branding.theme ?? 'auto';
  const theme = useMemo(
    () => resolvePublicStatusTheme({ visitorTheme, configuredTheme, prefersDark }),
    [configuredTheme, prefersDark, visitorTheme]
  );
  const isDark = theme === 'dark';
  const branding = statusQuery.data?.page.branding;

  useEffect(() => {
    const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');

    applyResolvedTheme(theme);
    if (themeColor) {
      themeColor.content = isDark ? '#020617' : branding?.backgroundColor || '#f8fafc';
    }

    return () => {
      applyThemePreference(readThemePreference());
    };
  }, [branding?.backgroundColor, isDark, prefersDark, theme]);

  const subscribeMutation = useMutation({
    mutationFn: () =>
      publicJson<{ success: true }>(`${apiPath}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }),
    onSuccess: () => {
      setEmail('');
      setSubscribed(true);
    },
  });

  if (statusQuery.isPending) {
    return <LoadingState />;
  }
  if (statusQuery.isError || !statusQuery.data) {
    return (
      <ErrorState
        message={
          statusQuery.error instanceof Error
            ? statusQuery.error.message
            : 'The public status page could not be loaded.'
        }
      />
    );
  }

  const data = statusQuery.data;
  const page = data.page;
  const settings = page.settings;
  const hasActiveIncident = data.incidents.active.length > 0;
  const hasInProgressMaintenance = data.maintenance.inProgress.length > 0;
  const disruptedComponent = data.components.find(
    (component) => component.status !== 'operational' && component.status !== 'up'
  );
  const overallState = hasActiveIncident
    ? { label: 'Active incident', status: 'partial_outage' }
    : disruptedComponent
      ? { label: statusLabel(disruptedComponent.status), status: disruptedComponent.status }
      : hasInProgressMaintenance
        ? { label: 'Maintenance in progress', status: 'maintenance' }
        : { label: 'All systems operational', status: 'operational' };
  const incidents = settings.showHistoricalIncidents
    ? [...data.incidents.active, ...data.incidents.historical]
    : data.incidents.active;
  const maintenance = [...data.maintenance.inProgress, ...data.maintenance.upcoming];
  const pageClass = isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900';
  const headerClass = isDark
    ? 'border-slate-800 bg-slate-950/95'
    : 'border-slate-200 bg-white/95';
  const mutedTextClass = isDark ? 'text-slate-400' : 'text-slate-600';
  const subtleTextClass = isDark ? 'text-slate-500' : 'text-slate-500';
  const cardClass = isDark
    ? 'border-slate-800 bg-slate-900/70'
    : 'border-slate-200 bg-white';
  const controlClass = isDark
    ? 'border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800'
    : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-100';
  const dividerClass = isDark ? 'border-slate-800' : 'border-slate-200';

  const handleThemeToggle = () => {
    const nextTheme = togglePublicStatusTheme(theme);
    setVisitorTheme(nextTheme);
    try {
      window.localStorage.setItem(publicStatusThemeStorageKey(slug), nextTheme);
    } catch {
      // The toggle still applies for this visit when storage is unavailable.
    }
  };

  return (
    <main
      className={`min-h-screen transition-colors ${pageClass}`}
      data-status-theme={theme}
      style={{
        backgroundColor: isDark ? '#020617' : page.branding.backgroundColor,
        color: isDark ? '#f8fafc' : page.branding.textColor,
      }}
    >
      <header className={`border-b backdrop-blur ${headerClass}`}>
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-5 py-8 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {page.branding.logo ? (
                <img
                  src={page.branding.logo}
                  alt={`${page.name} logo`}
                  className="max-h-9 max-w-40 object-contain"
                />
              ) : (
                <span
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: page.branding.primaryColor }}
                  aria-hidden="true"
                />
              )}
              <span className={`text-xs font-semibold uppercase tracking-[0.2em] ${mutedTextClass}`}>
                Perspectify status
              </span>
            </div>
            <button
              type="button"
              onClick={handleThemeToggle}
              aria-label="Dark mode"
              aria-pressed={isDark}
              title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${controlClass}`}
            >
              {isDark ? <Sun className="h-4 w-4" aria-hidden="true" /> : <Moon className="h-4 w-4" aria-hidden="true" />}
              {isDark ? 'Light mode' : 'Dark mode'}
            </button>
          </div>
          <div className="max-w-3xl">
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{page.name}</h1>
            {page.description ? (
              <p className={`mt-3 text-base ${mutedTextClass}`}>{page.description}</p>
            ) : null}
          </div>
        </div>
      </header>

      <div className="mx-auto w-full max-w-5xl px-5 py-8 sm:px-8">
        <section className={`border-b pb-7 ${dividerClass}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2
                className={`text-xl font-semibold ${statusTone(overallState.status, isDark)}`}
              >
                {overallState.label}
              </h2>
              <p className={`mt-1 text-sm ${mutedTextClass}`}>
                {data.components.length} public component{data.components.length === 1 ? '' : 's'} monitored
              </p>
            </div>
            {settings.showSubscription && settings.allowRSSSubscription ? (
              <a
                className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${controlClass}`}
                href={`${apiPath}/rss`}
              >
                RSS feed
              </a>
            ) : null}
          </div>
        </section>

        <section className="mt-8">
          <h2 className="text-lg font-semibold">System status</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            {data.components.length === 0 ? (
              <p className={`text-sm ${mutedTextClass}`}>No public components configured.</p>
            ) : null}
            {data.components.map((component) => (
              <article key={component.id} className={`rounded-xl border p-5 shadow-sm ${cardClass}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="font-semibold">{component.name}</h3>
                    {component.description ? (
                      <p className={`mt-1 text-sm ${mutedTextClass}`}>{component.description}</p>
                    ) : null}
                  </div>
                  <span className={`shrink-0 text-sm font-medium ${statusTone(component.status, isDark)}`}>
                    {statusLabel(component.status)}
                  </span>
                </div>
                {settings.showUptime && component.showUptime && component.uptime ? (
                  <div className="mt-5">
                    {component.uptime.data.length > 0 ? (
                      <div className="flex h-8 gap-0.5" aria-label={`${component.uptime.percentage.toFixed(2)} percent uptime`}>
                        {component.uptime.data.map((day) => (
                          <span
                            key={day.date}
                            aria-hidden="true"
                            className={`min-w-0 flex-1 rounded-sm ${
                              day.status === 'operational'
                                ? 'bg-emerald-500'
                                : day.status === 'major_outage'
                                  ? 'bg-rose-500'
                                  : 'bg-amber-500'
                            }`}
                            title={`${formatDate(day.date)}: ${day.percentage.toFixed(2)}%`}
                          />
                        ))}
                      </div>
                    ) : null}
                    <div className={`mt-2 flex justify-between text-xs ${subtleTextClass}`}>
                      <span>{settings.uptimeDays} days ago</span>
                      <span>{component.uptime.percentage.toFixed(2)}% uptime</span>
                      <span>Today</span>
                    </div>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-lg font-semibold">Incidents</h2>
          <div className="mt-4 grid gap-4">
            {incidents.length === 0 ? (
              <div className={`rounded-xl border p-5 text-sm ${cardClass} ${mutedTextClass}`}>
                No incidents reported.
              </div>
            ) : null}
            {incidents.map((incident) => (
              <article key={incident._id} className={`rounded-xl border p-5 shadow-sm ${cardClass}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold">{incident.title}</h3>
                    <p className={`mt-1 text-xs ${mutedTextClass}`}>{formatDate(incident.startedAt)}</p>
                  </div>
                  <span className={`text-sm font-medium ${statusTone(incident.status, isDark)}`}>
                    {statusLabel(incident.status)}
                  </span>
                </div>
                {latestIncidentUpdate(incident)?.message ? (
                  <p className={`mt-4 text-sm ${mutedTextClass}`}>
                    {latestIncidentUpdate(incident)?.message}
                  </p>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        {maintenance.length > 0 ? (
          <section className="mt-10">
            <h2 className="text-lg font-semibold">Maintenance</h2>
            <div className="mt-4 grid gap-4">
              {maintenance.map((window) => (
                <article key={window._id} className={`rounded-xl border p-5 shadow-sm ${cardClass}`}>
                  <h3 className="font-semibold">{window.title}</h3>
                  <p className={`mt-2 text-sm ${mutedTextClass}`}>
                    {formatDate(window.scheduledStart)} – {formatDate(window.scheduledEnd)}
                  </p>
                  {window.description ? (
                    <p className={`mt-3 text-sm ${mutedTextClass}`}>{window.description}</p>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {settings.showSubscription && settings.allowEmailSubscription ? (
          <section className={`mt-10 border-t pt-8 ${dividerClass}`}>
            <h2 className="text-lg font-semibold">Status notifications</h2>
            <p className={`mt-2 text-sm ${mutedTextClass}`}>Receive incident and maintenance updates by email.</p>
            <form
              className="mt-4 flex max-w-2xl flex-col gap-3 sm:flex-row"
              onSubmit={(event) => {
                event.preventDefault();
                setSubscribed(false);
                subscribeMutation.mutate();
              }}
            >
              <label className="sr-only" htmlFor="status-subscription-email">
                Email address
              </label>
              <input
                id="status-subscription-email"
                className={`min-w-0 flex-1 rounded-md border px-3 py-2 text-sm ${controlClass}`}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Email address"
                type="email"
                required
              />
              <button
                type="submit"
                className="rounded-md px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-60"
                style={{ backgroundColor: page.branding.primaryColor }}
                disabled={subscribeMutation.isPending}
              >
                {subscribeMutation.isPending ? 'Subscribing…' : 'Subscribe'}
              </button>
            </form>
            {subscribed ? (
              <p className="mt-3 text-sm text-emerald-600" role="status">
                Subscription confirmed.
              </p>
            ) : null}
            {subscribeMutation.isError ? (
              <p className="mt-3 text-sm text-rose-600" role="alert">
                {subscribeMutation.error.message}
              </p>
            ) : null}
          </section>
        ) : null}

        <footer className={`mt-12 flex flex-wrap items-center justify-center gap-x-4 border-t py-6 text-center text-xs ${dividerClass} ${mutedTextClass}`}>
          {settings.showPoweredBy ? (
            <span>
              Powered by{' '}
              <a className="font-medium underline underline-offset-4" href="https://perspectify.dev">
                Perspectify
              </a>
            </span>
          ) : null}
          {/* Perspectify's own visitor consent for this page, not the status page owner's. */}
          <PrivacyChoicesButton variant="link" inheritColor />
        </footer>
      </div>
    </main>
  );
}
